import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  createInventoryNight,
  getInventoryNights,
  recalculateInventoryNight,
  submitInventoryNight,
  cancelInventoryNight,
  approveInventoryNight,
  rejectInventoryNight,
} from "../../api/inventoryNight";
import { isHrFamilyRole, isOwnerRole } from "../../utils/hrStaffHelpers";
import "./InventoryNight.css";

const ROLE_OPTIONS = [
  { value: "ACCOUNTANT", label: "محاسب" },
  { value: "CASHIER", label: "كاشير" },
  { value: "MANAGER_OR_TL", label: "مدير / تيم ليدر" },
  { value: "SALES", label: "سيلز" },
];

const STATUS_LABELS = {
  DRAFT: "مسودة",
  PENDING: "بانتظار الاعتماد",
  APPROVED: "معتمد",
  COMPLETED: "مكتمل / مُسوّى",
  REJECTED: "مرفوض",
  CANCELLED: "ملغي",
};

const COMP_LABELS = {
  PAID_OT: "إضافي مدفوع",
  COMPENSATORY: "رصيد تعويضي",
};

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function emptyMember() {
  return {
    employeeId: "",
    role: "SALES",
    compensationType: "PAID_OT",
  };
}

export default function InventoryNightPage() {
  const { user } = useAuth();
  const { branches, employeesByBranch, refreshData } = useData();
  const isHr = isHrFamilyRole(user?.role) || user?.role === "gm";
  const isReadOnly = isOwnerRole(user?.role);

  const [nights, setNights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState("");

  const userBranchId = user?.branchId || user?.employee?.branchId || "";
  const [branchId, setBranchId] = useState(userBranchId);
  const [scheduledDate, setScheduledDate] = useState("");
  const [plannedStart, setPlannedStart] = useState("22:00");
  const [reason, setReason] = useState("");
  const [members, setMembers] = useState([
    emptyMember(),
    { ...emptyMember(), role: "ACCOUNTANT" },
    { ...emptyMember(), role: "CASHIER" },
    { ...emptyMember(), role: "MANAGER_OR_TL" },
  ]);

  const branchEmployees = useMemo(() => {
    const all = Object.values(employeesByBranch || {}).flat();
    if (!branchId) return all.filter((e) => e.status !== "terminated");
    return all.filter(
      (e) => e.branchId === branchId && e.status !== "terminated",
    );
  }, [employeesByBranch, branchId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await getInventoryNights(
        branchId ? { branchId } : undefined,
      );
      setNights(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل الجرد"));
      setNights([]);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!branchId && userBranchId) setBranchId(userBranchId);
  }, [userBranchId, branchId]);

  function updateMember(index, patch) {
    setMembers((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    );
  }

  function addMember() {
    setMembers((prev) => [...prev, emptyMember()]);
  }

  function removeMember(index) {
    setMembers((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleCreate(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!branchId || !scheduledDate) {
      setError("اختر الفرع والتاريخ");
      return;
    }
    const validMembers = members.filter((m) => m.employeeId);
    if (!validMembers.length) {
      setError("أضف عضواً واحداً على الأقل في اللجنة");
      return;
    }

    setSubmitting(true);
    try {
      await createInventoryNight({
        branchId,
        scheduledDate,
        plannedStart,
        reason: reason || undefined,
        members: validMembers.map((m) => ({
          employeeId: m.employeeId,
          role: m.role,
          compensationType: m.compensationType,
        })),
      });
      setSuccess("تم إنشاء الجرد كمسودة — راجع الساعات ثم أرسل للاعتماد");
      setReason("");
      setMembers([
        emptyMember(),
        { ...emptyMember(), role: "ACCOUNTANT" },
        { ...emptyMember(), role: "CASHIER" },
        { ...emptyMember(), role: "MANAGER_OR_TL" },
      ]);
      await load();
      await refreshData?.();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إنشاء الجرد"));
    } finally {
      setSubmitting(false);
    }
  }

  async function runAction(id, action) {
    setError("");
    setSuccess("");
    setActionId(`${action}-${id}`);
    try {
      if (action === "recalculate") {
        await recalculateInventoryNight(id);
        setSuccess("تم إعادة حساب الساعات من الحضور");
      } else if (action === "submit") {
        await submitInventoryNight(id);
        setSuccess("تم اعتماد الجرد وتسوية ساعات الزيادة");
      } else if (action === "cancel") {
        await cancelInventoryNight(id);
        setSuccess("تم إلغاء الجرد");
      } else if (action === "approve") {
        await approveInventoryNight(id);
        setSuccess("تم الاعتماد وتسوية ساعات الزيادة");
      } else if (action === "reject") {
        await rejectInventoryNight(id, { notes: "مرفوض من الواجهة" });
        setSuccess("تم رفض الجرد");
      }
      await load();
      await refreshData?.();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تنفيذ الإجراء"));
    } finally {
      setActionId("");
    }
  }

  return (
    <div className="inv-night-page" dir="rtl">
      <header className="inv-night-header">
        <h1>الجرد</h1>
        <p>
          شاشة موارد البشرية لتسجيل لجنة الجرد. الساعات تُحسب من بداية الجرد حتى
          انصراف البصمة — المتوقع يُكمَّل أولاً والزيادة فقط تُعوَّض.
        </p>
      </header>

      {error && <div className="inv-night-alert inv-night-alert-error">{error}</div>}
      {success && (
        <div className="inv-night-alert inv-night-alert-ok">{success}</div>
      )}

      {!isReadOnly && (
      <section className="inv-night-card">
        <h2>إنشاء جرد</h2>
        <form onSubmit={handleCreate} className="inv-night-form">
          <div className="inv-night-grid">
            <label>
              الفرع
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                required
              >
                <option value="">اختر الفرع</option>
                {(branches || []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              التاريخ
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                required
              />
            </label>
            <label>
              بداية الجرد
              <input
                type="time"
                value={plannedStart}
                onChange={(e) => setPlannedStart(e.target.value)}
                required
              />
            </label>
          </div>
          <label className="inv-night-full">
            السبب / ملاحظات
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="اختياري"
            />
          </label>

          <div className="inv-night-members">
            <div className="inv-night-members-head">
              <h3>لجنة الجرد</h3>
              <button type="button" className="inv-btn ghost" onClick={addMember}>
                + عضو
              </button>
            </div>
            {members.map((m, index) => (
              <div key={index} className="inv-night-member-row">
                <select
                  value={m.employeeId}
                  onChange={(e) =>
                    updateMember(index, { employeeId: e.target.value })
                  }
                >
                  <option value="">الموظف</option>
                  {branchEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name || emp.fullName} ({emp.code})
                    </option>
                  ))}
                </select>
                <select
                  value={m.role}
                  onChange={(e) => updateMember(index, { role: e.target.value })}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <select
                  value={m.compensationType}
                  onChange={(e) =>
                    updateMember(index, { compensationType: e.target.value })
                  }
                >
                  <option value="PAID_OT">إضافي مدفوع</option>
                  <option value="COMPENSATORY">رصيد تعويضي</option>
                </select>
                <button
                  type="button"
                  className="inv-btn danger"
                  onClick={() => removeMember(index)}
                  disabled={members.length <= 1}
                >
                  حذف
                </button>
              </div>
            ))}
          </div>

          <button type="submit" className="inv-btn primary" disabled={submitting}>
            {submitting ? "جارٍ الحفظ..." : "حفظ مسودة"}
          </button>
        </form>
      </section>
      )}

      <section className="inv-night-card">
        <div className="inv-night-members-head">
          <h2>سجلات الجرد</h2>
          <button type="button" className="inv-btn ghost" onClick={load}>
            تحديث
          </button>
        </div>
        {loading ? (
          <p>جارٍ التحميل...</p>
        ) : nights.length === 0 ? (
          <p className="inv-muted">لا توجد سجلات جرد بعد</p>
        ) : (
          <div className="inv-night-list">
            {nights.map((night) => (
              <article key={night.id} className="inv-night-item">
                <div className="inv-night-item-head">
                  <div>
                    <strong>{night.branch?.name || "فرع"}</strong>
                    <span className="inv-muted">
                      {" "}
                      — {night.scheduledDate} (بداية {night.plannedStart})
                    </span>
                    {night.isNightPolicy && (
                      <span className="inv-badge">ليلي</span>
                    )}
                  </div>
                  <span className={`inv-status inv-status-${night.status}`}>
                    {STATUS_LABELS[night.status] || night.status}
                  </span>
                </div>

                <div className="inv-table-wrap">
                  <table className="inv-table">
                    <thead>
                      <tr>
                        <th>الموظف</th>
                        <th>الدور</th>
                        <th>فرع</th>
                        <th>جرد</th>
                        <th>متوقع</th>
                        <th>عادي</th>
                        <th>زيادة</th>
                        <th>نقص</th>
                        <th>معلّق</th>
                        <th>تعويض</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(night.members || []).map((m) => (
                        <tr key={m.id}>
                          <td>{m.employee?.fullName}</td>
                          <td>
                            {ROLE_OPTIONS.find((r) => r.value === m.role)
                              ?.label || m.role}
                          </td>
                          <td>{m.branchHours}</td>
                          <td>{m.inventoryHours}</td>
                          <td>{m.expectedHours}</td>
                          <td>{m.regularHours}</td>
                          <td>
                            <strong>{m.overtimeHours}</strong>
                          </td>
                          <td>{m.shortageHours || 0}</td>
                          <td>
                            {Number(m.pendingShortageHours) > 0 ? (
                              <strong className="inv-pending-shortage">
                                {m.pendingShortageHours}
                              </strong>
                            ) : (
                              m.pendingShortageHours || 0
                            )}
                          </td>
                          <td>{COMP_LABELS[m.compensationType] || m.compensationType}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="inv-night-actions">
                  {!isReadOnly && (night.status === "DRAFT" || night.status === "REJECTED") && (
                    <>
                      <button
                        type="button"
                        className="inv-btn ghost"
                        disabled={actionId === `recalculate-${night.id}`}
                        onClick={() => runAction(night.id, "recalculate")}
                      >
                        إعادة حساب من البصمة
                      </button>
                      <button
                        type="button"
                        className="inv-btn primary"
                        disabled={actionId === `submit-${night.id}`}
                        onClick={() => runAction(night.id, "submit")}
                      >
                        اعتماد وتسوية
                      </button>
                      <button
                        type="button"
                        className="inv-btn danger"
                        disabled={actionId === `cancel-${night.id}`}
                        onClick={() => runAction(night.id, "cancel")}
                      >
                        إلغاء
                      </button>
                    </>
                  )}
                  {night.status === "PENDING" && isHr && !isReadOnly && (
                    <>
                      <button
                        type="button"
                        className="inv-btn primary"
                        disabled={actionId === `approve-${night.id}`}
                        onClick={() => runAction(night.id, "approve")}
                      >
                        اعتماد وتسوية
                      </button>
                      <button
                        type="button"
                        className="inv-btn danger"
                        disabled={actionId === `reject-${night.id}`}
                        onClick={() => runAction(night.id, "reject")}
                      >
                        رفض
                      </button>
                    </>
                  )}
                  {night.status === "PENDING" && !isHr && !isReadOnly && (
                    <button
                      type="button"
                      className="inv-btn danger"
                      disabled={actionId === `cancel-${night.id}`}
                      onClick={() => runAction(night.id, "cancel")}
                    >
                      إلغاء الطلب
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
